import React, { useEffect, useState } from 'react';
import { RefreshCw, Eye, CheckCircle, XCircle, Clock, X, User, Building, MapPin, Phone, Mail, CreditCard, Calendar } from 'lucide-react';
import { API_URLS } from '../../../config/api';
import { adminApiFetch } from '../../../utils/adminApi';

export default function AdminMarkaTescilApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const fetchApplications = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminApiFetch(API_URLS.ADMIN.MARKA_TESCIL.APPLICATIONS);
      const data = await res.json();
      setApplications(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      setError('Başvurular yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-600" />;
      default: return <Clock className="w-4 h-4 text-yellow-600" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return 'Onaylandı';
      case 'rejected': return 'Reddedildi';
      default: return 'Beklemede';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const updateApplicationStatus = async (applicationId, newStatus) => {
    setUpdatingStatus(applicationId);
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.MARKA_TESCIL.UPDATE_APPLICATION_STATUS, {
        method: 'PUT',
        body: JSON.stringify({
          id: applicationId,
          status: newStatus
        })
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        // Update the application in the local state
        setApplications(prev => prev.map(app => 
          app.id === applicationId 
            ? { ...app, status: newStatus }
            : app
        ));
        
        // Update selected app if it's the same one
        if (selectedApp && selectedApp.id === applicationId) {
          setSelectedApp(prev => ({ ...prev, status: newStatus }));
        }
      } else {
        setError('Durum güncellenemedi: ' + result.message);
      }
    } catch (e) {
      setError('Durum güncellenemedi');
    } finally {
      setUpdatingStatus(null);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  return (
    <div className="px-2 sm:px-0">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-bold">Marka Tescil Başvuruları</h2>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button onClick={fetchApplications} className="h-9 px-3 rounded-md border border-gray-200 hover:bg-gray-50 text-xs sm:text-sm flex items-center gap-2 flex-1 sm:flex-initial justify-center">
            <RefreshCw className="w-4 h-4" /> <span className="hidden sm:inline">Yenile</span>
          </button>
        </div>
      </div>

      {error && <div className="mb-4 text-xs sm:text-sm text-red-600">{error}</div>}

      <div className="border border-gray-100 rounded-xl overflow-hidden">
        {/* Desktop Table Header */}
        <div className="hidden sm:grid grid-cols-12 bg-gray-50 text-xs sm:text-sm font-semibold text-gray-700 px-4 py-2">
          <div className="col-span-2">Marka Adı</div>
          <div className="col-span-2">Tip</div>
          <div className="col-span-1">Sınıf Sayısı</div>
          <div className="col-span-2">Toplam Tutar</div>
          <div className="col-span-2">Durum</div>
          <div className="col-span-2">Tarih</div>
          <div className="col-span-1 text-right">İşlemler</div>
        </div>
        
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Yükleniyor...</div>
        ) : applications.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">Başvuru bulunamadı.</div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden sm:block">
              {applications.map((app, i) => (
                <div key={i} className="grid grid-cols-12 items-center px-4 py-3 border-t text-sm hover:bg-gray-50">
                  <div className="col-span-2 font-medium">{app.marka_adi || '-'}</div>
                  <div className="col-span-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      app.application_type === 'bireysel' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {app.application_type === 'bireysel' ? 'Bireysel' : 'Kurumsal'}
                    </span>
                  </div>
                  <div className="col-span-1">{app.selected_classes?.length || 0}</div>
                  <div className="col-span-2">₺{parseFloat(app.total_amount || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
                  <div className="col-span-2 flex items-center gap-2">
                    {getStatusIcon(app.status)}
                    <span className="text-sm">{getStatusText(app.status)}</span>
                  </div>
                  <div className="col-span-2 text-gray-500">{formatDate(app.created_at)}</div>
                  <div className="col-span-1 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button 
                        onClick={() => setSelectedApp(app)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Detayları Görüntüle"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <div className="relative">
                        <select
                          value={app.status}
                          onChange={(e) => updateApplicationStatus(app.id, e.target.value)}
                          disabled={updatingStatus === app.id}
                          className="text-xs px-2 py-1 border rounded-md bg-white"
                        >
                          <option value="pending">Beklemede</option>
                          <option value="approved">Onaylandı</option>
                          <option value="rejected">Reddedildi</option>
                        </select>
                        {updatingStatus === app.id && (
                          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Card View */}
            <div className="sm:hidden space-y-3 p-3">
              {applications.map((app, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                  <div>
                    <div className="font-semibold text-sm mb-1">{app.marka_adi || '-'}</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        app.application_type === 'bireysel' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {app.application_type === 'bireysel' ? 'Bireysel' : 'Kurumsal'}
                      </span>
                      <span className="text-xs text-gray-500">{app.selected_classes?.length || 0} Sınıf</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Toplam:</span>{' '}
                      <span className="font-semibold text-green-600">₺{parseFloat(app.total_amount || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {getStatusIcon(app.status)}
                      <span className="text-xs">{getStatusText(app.status)}</span>
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-500">{formatDate(app.created_at)}</div>
                  
                  <div className="flex items-center justify-end gap-2 pt-2 border-t">
                    <button 
                      onClick={() => setSelectedApp(app)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded text-xs flex items-center gap-1"
                    >
                      <Eye className="w-4 h-4" /> Detay
                    </button>
                    <div className="relative">
                      <select
                        value={app.status}
                        onChange={(e) => updateApplicationStatus(app.id, e.target.value)}
                        disabled={updatingStatus === app.id}
                        className="text-xs px-2 py-1.5 border rounded-md bg-white"
                      >
                        <option value="pending">Beklemede</option>
                        <option value="approved">Onaylandı</option>
                        <option value="rejected">Reddedildi</option>
                      </select>
                      {updatingStatus === app.id && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Application Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedApp(null)} />
          <div className="relative bg-white w-full max-w-4xl rounded-xl shadow-xl border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b sticky top-0 bg-white">
              <h3 className="text-base sm:text-lg font-bold">Başvuru Detayları</h3>
            </div>
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-700">Marka Adı</label>
                  <p className="text-sm font-medium">{selectedApp.marka_adi}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-700">Başvuru Tipi</label>
                  <p className="text-sm font-medium">{selectedApp.application_type === 'bireysel' ? 'Bireysel' : 'Kurumsal'}</p>
                </div>
              </div>

              {selectedApp.application_type === 'bireysel' && selectedApp.individual_data && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Bireysel Bilgiler
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div><label className="text-xs sm:text-sm text-gray-700">Ad Soyad:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.individual_data.ad_soyad || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">TC Kimlik No:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.individual_data.tc_kimlik_no || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">Telefon:</label> <span className="text-xs sm:text-sm flex items-center gap-1 mt-0.5"><Phone className="w-4 h-4" />{selectedApp.individual_data.telefon || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">Ülke:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.individual_data.ulke || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">Şehir:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.individual_data.sehir || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">İlçe:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.individual_data.ilce || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">Posta Kodu:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.individual_data.posta_kodu || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">Faks:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.individual_data.faks || '-'}</span></div>
                  </div>
                  <div className="mt-3">
                    <label className="text-sm text-gray-700 flex items-center gap-1"><MapPin className="w-4 h-4" />Adres:</label>
                    <p className="text-sm mt-1">{selectedApp.individual_data.adres || '-'}</p>
                  </div>
                </div>
              )}

              {selectedApp.application_type === 'kurumsal' && selectedApp.corporate_data && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Building className="w-5 h-5 text-purple-600" />
                    Kurumsal Bilgiler
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div><label className="text-xs sm:text-sm text-gray-700">Şirket Unvanı:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.corporate_data.sirket_unvani || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">Vergi Dairesi:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.corporate_data.vergi_dairesi || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">Vergi No:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.corporate_data.vergi_no || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">Telefon:</label> <span className="text-xs sm:text-sm flex items-center gap-1 mt-0.5"><Phone className="w-4 h-4" />{selectedApp.corporate_data.telefon || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">Ülke:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.corporate_data.ulke || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">Şehir:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.corporate_data.sehir || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">İlçe:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.corporate_data.ilce || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">Posta Kodu:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.corporate_data.posta_kodu || '-'}</span></div>
                    <div><label className="text-xs sm:text-sm text-gray-700">Faks:</label> <span className="text-xs sm:text-sm block mt-0.5">{selectedApp.corporate_data.faks || '-'}</span></div>
                  </div>
                  <div className="mt-3">
                    <label className="text-sm text-gray-700 flex items-center gap-1"><MapPin className="w-4 h-4" />Adres:</label>
                    <p className="text-sm mt-1">{selectedApp.corporate_data.adres || '-'}</p>
                  </div>
                </div>
              )}

              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-600" />
                  Seçilen Marka Sınıfları ({selectedApp.selected_classes?.length || 0})
                </h4>
                <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                  {selectedApp.selected_classes?.map((cls, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border">
                      <div>
                        <span className="text-sm font-medium">{cls.code} - {cls.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Kurum Harcı: ₺{parseFloat(cls.kurum_harc || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
                        <div className="text-sm text-gray-600">Hizmet Bedeli: ₺{parseFloat(cls.hizmet_bedeli || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
                        <div className="text-sm font-semibold text-green-600">Toplam: ₺{(parseFloat(cls.kurum_harc || 0) + parseFloat(cls.hizmet_bedeli || 0)).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs sm:text-sm text-gray-700 flex items-center gap-1">
                    <CreditCard className="w-4 h-4" />
                    Toplam Tutar
                  </label>
                  <p className="text-xl sm:text-2xl font-bold text-green-600 mt-1">₺{parseFloat(selectedApp.total_amount || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</p>
                </div>
                <div>
                  <label className="text-xs sm:text-sm text-gray-700 flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Durum
                  </label>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 mt-1">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(selectedApp.status)}
                      <span className="text-xs sm:text-sm font-medium">{getStatusText(selectedApp.status)}</span>
                    </div>
                    <div className="relative w-full sm:w-auto">
                      <select
                        value={selectedApp.status}
                        onChange={(e) => updateApplicationStatus(selectedApp.id, e.target.value)}
                        disabled={updatingStatus === selectedApp.id}
                        className="text-xs sm:text-sm px-3 py-1.5 border rounded-md bg-white w-full"
                      >
                        <option value="pending">Beklemede</option>
                        <option value="approved">Onaylandı</option>
                        <option value="rejected">Reddedildi</option>
                      </select>
                      {updatingStatus === selectedApp.id && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-t flex items-center justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setSelectedApp(null)} className="h-10 px-4 rounded-md border border-gray-200 text-sm sm:text-base w-full sm:w-auto">Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
